import type { CSSProperties } from 'react';
import { useState, useEffect, useMemo } from 'react';
import PageHeader from '@/components/PageHeader';
import { useMetaTags } from '@/hooks/useMetaTags';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Users, Images, FolderHeart, Loader2, Trophy, ChevronsRight, ImagePlay, Link as LinkIcon, NotebookPen, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAlbumUsers } from '@/hooks/useAlbumUsers';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { useTheme } from '@/contexts/ThemeProvider';
import { useAlbumNavigation } from "@/hooks/useAlbumNavigation";
import * as Sentry from "@sentry/react";
import { useTranslation } from '@/hooks/useTranslation';
import { useSettings } from '@/hooks/useSettings';
import { getSurfaceContrastColors, toSurfaceContrastVariables } from '@/lib/contrastUtils';


interface panelStats {
  totalUsers: number;
  activeUsers: number;
  totalPhotos: number;
  totalVoted: number;
  totalFlagged: number;
  totalMessages: number;
}

type MediaVoteStatRow = {
  id: string;
  votes_count?: number | null;
};

const AdminPanel = () => {
  const { albumId } = useAlbumContext();
  const { tokens } = useTheme();
  const { toast } = useToast();
  const { getAlbumPath } = useAlbumNavigation();
  const { t, plural } = useTranslation();

  useMetaTags({
    title: t('adminMetaTitle'),
    pathname: '/admin'
  });

  const { totalCount, activeCount, loading: usersLoading } = useAlbumUsers();
  const { getBooleanSetting } = useSettings();
  const isLikesEnabled = getBooleanSetting('enable_likes', false);
  const isModerationEnabled = getBooleanSetting('enable_moderation', false);
  const isGuestbookEnabled = getBooleanSetting('enable_guestbook', false);
  const [stats, setStats] = useState<panelStats>({
    totalUsers: 0,
    activeUsers: 0,
    totalPhotos: 0,
    totalVoted: 0,
    totalFlagged: 0,
    totalMessages: 0
  });
  const [loading, setLoading] = useState(true);
  // Temporary random access key for screen content
  const [screenKey, setScreenKey] = useState<string>('');
  const cardSurface = tokens.colors.card || tokens.colors.background || '0 0% 100%';
  const cardText = tokens.colors['card-foreground'] || tokens.colors.foreground;
  const cardSurfaceStyle = useMemo<CSSProperties>(
    () => toSurfaceContrastVariables(getSurfaceContrastColors(cardSurface, cardText)) as CSSProperties,
    [cardSurface, cardText],
  );
  const headingTopOffsetStyle = useMemo<CSSProperties>(
    () => ({ paddingTop: 'var(--heading-top-offset, 0px)' }),
    [],
  );

  const copyToClipboard = async (text: string, successMessageKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: t('copied'),
        description: t(successMessageKey)
      });
    } catch (error) {
      Sentry.captureMessage(`Error copying to clipboard: ${error instanceof Error ? error.message : String(error)}`, "error");
      console.error('Error copying to clipboard:', error);
      toast({
        title: t('error'),
        description: t('copyToClipboardFailed'),
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // console.log('Fetching stats for albumId:', albumId);

        // Use RPC to get all media for the album and compute stats
        const { data: mediaRows, error: mediaErr } = await supabase
          .rpc('get_media_with_votes_filtered', { p_album_id: albumId, p_exclude_flagged: false } as never);
        if (mediaErr) throw mediaErr;
        const typedMediaRows = (mediaRows ?? []) as MediaVoteStatRow[];
        const totalPhotos = typedMediaRows.length;
        const totalVoted = new Set(
          typedMediaRows
            .filter((row) => (row.votes_count ?? 0) > 0)
            .map((row) => row.id)
        ).size;

        const flaggedQuery = await supabase
          .from('media')
          .select('*', { count: 'exact', head: true })
          .eq('album_id', albumId)
          .eq('flagged', true);

        // Fetch messages count if guestbook is enabled
        let totalMessages = 0;
        if (isGuestbookEnabled) {
          const messagesQuery = await supabase
            .from('messages')
            .select('*', { count: 'exact', head: true })
            .eq('album_id', albumId);
          totalMessages = messagesQuery.count || 0;
        }

        const newStats = {
          totalPhotos,
          totalVoted,
          totalFlagged: flaggedQuery.count || 0,
          totalMessages
        };

        // console.log('New stats:', newStats);

        setStats(prev => ({
          ...prev,
          ...newStats
        }));
      } catch (error) {
        Sentry.captureMessage(`Error fetching photos: ${error instanceof Error ? error.message : String(error)}`, "error");
        console.error('Error fetching photos:', error);
      } finally {
        setLoading(false);
      }
    };

    // Fetch screenKey from settings table
    const fetchScreenKey = async () => {
      try {
        const { data, error } = await supabase
          .from('settings')
          .select('value')
          .eq('album_id', albumId)
          .eq('key', 'screenKey')
          .single();

        if (error) throw error;
        if (data?.value) {
          setScreenKey(data.value);
        }
      } catch (error) {
        Sentry.captureMessage(`Error fetching screenKey: ${error instanceof Error ? error.message : String(error)}`, "error");
        console.error('Error fetching screenKey:', error);
      }
    };

    if (albumId) {
      fetchData();
      fetchScreenKey();

      // Set up real-time subscription for media changes
      const channel = supabase
        .channel('media-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'media',
            filter: `album_id=eq.${albumId}`
          },
          (payload) => {
            // console.log('Real-time media change:', payload);
            fetchData(); // Refresh stats when media changes
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'media_votes'
          },
          (payload) => {
            // console.log('Real-time vote change:', payload);
            fetchData(); // Refresh stats when votes change
          }
        )
        .subscribe();

      const interval = setInterval(() => {
        fetchData();
        fetchScreenKey();
      }, 5000); // Reduced to 5 seconds for more responsive updates

      return () => {
        supabase.removeChannel(channel);
        clearInterval(interval);
      };
    }
  }, [albumId, isGuestbookEnabled]);

  // Refresh stats when user focuses on the page (e.g., returning from fotos page)
  useEffect(() => {
    const handleFocus = () => {
      if (albumId) {
        const refreshData = async () => {
          try {
            // console.log('Page focused - refreshing stats for albumId:', albumId);

            // Use RPC to get all media for the album and compute stats
            const { data: mediaRows, error: mediaErr } = await supabase
              .rpc('get_media_with_votes_filtered', { p_album_id: albumId, p_exclude_flagged: false } as never);
            if (mediaErr) throw mediaErr;
            const typedMediaRows = (mediaRows ?? []) as MediaVoteStatRow[];
            const totalPhotos = typedMediaRows.length;
            const totalVoted = new Set(
              typedMediaRows
                .filter((row) => (row.votes_count ?? 0) > 0)
                .map((row) => row.id)
            ).size;

            const flaggedQuery = await supabase
              .from('media')
              .select('*', { count: 'exact', head: true })
              .eq('album_id', albumId)
              .eq('flagged', true);

            const newStats = {
              totalPhotos,
              totalVoted,
              totalFlagged: flaggedQuery.count || 0
            };

            // console.log('Focus refresh - New stats:', newStats);

            setStats(prev => ({
              ...prev,
              ...newStats
            }));
          } catch (error) {
            Sentry.captureMessage(`Error fetching photos on focus: ${error instanceof Error ? error.message : String(error)}`, "error");
            console.error('Error fetching photos on focus:', error);
          }
        };
        refreshData();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [albumId]);

  useEffect(() => {
    setStats(prev => ({ ...prev, totalUsers: totalCount, activeUsers: activeCount }));
  }, [totalCount, activeCount]);

  if (loading || usersLoading) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title={t('adminPanelTitle')} />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title={t('adminPanelTitle')} />

      <div className="max-w-4xl mx-auto px-4 py-8">

        <div className="space-y-6">


          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Usuarios Widget */}
            <Card className="relative overflow-hidden" style={cardSurfaceStyle}>
              <div className="flex h-full">
                <div className="flex-1">
                  <CardHeader className="pb-3">
                    <div className="flex items-center space-x-2">
                      <Users className="w-5 h-5 text-[hsl(var(--surface-fg))]" />
                      <div style={headingTopOffsetStyle}>
                        <CardTitle className="text-lg">{t('usersTitle')}</CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-[hsl(var(--surface-fg))]">
                          {stats.totalUsers}
                        </div>
                        <div className="text-sm text-[hsl(var(--surface-muted-fg))]">
                          {plural('registeredUser', 'registeredUsers', stats.totalUsers)}
                        </div>
                      </div>
                      <div className="text-center">
                        <div
                          className="text-3xl font-bold"
                          data-number={stats.activeUsers}
                        >
                          {stats.activeUsers}
                        </div>
                        <div className="text-sm text-[hsl(var(--surface-muted-fg))]">
                          {t('activeNow', { count: stats.activeUsers })}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </div>
                <Link to={getAlbumPath("/admin/usuarios")} className="w-20">
                  <div className="h-full w-full flex items-center justify-center transition-colors duration-200 bg-accent hover:bg-accent/80">
                    <ChevronsRight className="w-5 h-5 text-accent-foreground" />
                  </div>
                </Link>
              </div>
            </Card>

            {/* Fotos Widget */}
            <Card className="relative overflow-hidden" style={cardSurfaceStyle}>
              <div className="flex h-full">
                <div className="flex-1">
                  <CardHeader className="pb-3">
                    <div className="flex items-center space-x-2">
                      <Images className="w-5 h-5 text-[hsl(var(--surface-fg))]" />
                      <div style={headingTopOffsetStyle}>
                        <CardTitle className="text-lg">{t('photosTitle')}</CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className={`flex justify-evenly`}>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[hsl(var(--surface-fg))]">
                          {stats.totalPhotos}
                        </div>
                        <div className="text-xs text-[hsl(var(--surface-muted-fg))]">
                          {t('uploadedShort')}
                        </div>
                      </div>
                      {isLikesEnabled && (
                        <div className="text-center">
                          <div className="text-2xl font-bold text-[hsl(var(--surface-fg))]">
                            {stats.totalVoted}
                          </div>
                          <div className="text-xs text-[hsl(var(--surface-muted-fg))]">
                            {t('votedShort')}
                          </div>
                        </div>
                      )}
                      {isModerationEnabled && (
                        <div className="text-center">
                          <Link to={getAlbumPath("/admin/fotos?moderar")} >
                            <div className="text-2xl font-bold text-[hsl(var(--surface-fg))]">
                              {stats.totalFlagged}
                            </div>
                            <div className="text-xs text-[hsl(var(--surface-muted-fg))]">
                              {t('moderatedShort')}
                            </div>
                          </Link>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </div>
                <Link to={getAlbumPath("/admin/fotos")} className="w-20">
                  <div className="h-full w-full flex items-center justify-center transition-colors duration-200 bg-accent hover:bg-accent/80">
                    <ChevronsRight className="w-5 h-5 text-accent-foreground" />
                  </div>
                </Link>
              </div>
            </Card>

            {/* Mensajes Widget */}
            {isGuestbookEnabled && (
              <Card className="relative overflow-hidden" style={cardSurfaceStyle}>
                <div className="flex h-full">
                  <div className="flex-1">
                    <CardHeader className="pb-3">
                      <div className="flex items-center space-x-2">
                        <NotebookPen className="w-5 h-5 text-[hsl(var(--surface-fg))]" />
                        <div style={headingTopOffsetStyle}>
                          <CardTitle className="text-lg">Libro de Visitas</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-[hsl(var(--surface-fg))]">
                          {stats.totalMessages}
                        </div>
                        <div className="text-sm text-[hsl(var(--surface-muted-fg))]">
                          {stats.totalMessages === 1 ? 'mensaje' : 'mensajes'}
                        </div>
                      </div>
                    </CardContent>
                  </div>
                  <Link to={getAlbumPath("/mensajes")} className="w-20">
                    <div className="h-full w-full flex items-center justify-center transition-colors duration-200 bg-accent hover:bg-accent/80">
                      <ChevronsRight className="w-5 h-5 text-accent-foreground" />
                    </div>
                  </Link>
                </div>
              </Card>
            )}
          </div>


          {/* Screen Content wrapper */}
          <Card style={cardSurfaceStyle}>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column: description + access key */}
                <div className="space-y-4">
                  <div>
                    <div style={headingTopOffsetStyle}>
                      <h3 className="text-xl font-medium">{t('screenContentTitle')}</h3>
                    </div>
                    <p className="mt-2 text-sm text-[hsl(var(--surface-muted-fg))]">
                      {t('screenContentDescription')}
                    </p>
                  </div>
                  <div>
                    <img
                      src="/img/slideshow.png"
                      alt="Slideshow preview"
                      className="w-full rounded-lg shadow-md object-cover hidden md:block pointer-events-none"
                    />
                  </div>

                </div>

                {/* Right column: stacked cards */}
                <div className="space-y-4">

                  <Card style={cardSurfaceStyle}>

                    <CardHeader className="w-full">
                      <div className="flex w-full items-center justify-between">
                        <div>
                          <div className="flex items-center">
                            <ImagePlay className="mr-3 w-5 h-5 text-[hsl(var(--surface-fg))]" />
                            <div style={headingTopOffsetStyle}>
                              <CardTitle className='text-md md:text-ls'>{t('liveGallery')}</CardTitle>
                            </div>
                          </div>
                          <p className="mr-4 mt-2 text-sm text-[hsl(var(--surface-muted-fg))]">
                            {t('screenContentDescriptionLiveGallery')}
                          </p>
                        </div>
                        <div className="flex flex-col items-end space-y-2">
                          <Link to={getAlbumPath(`/galeria-en-vivo?code=${screenKey}`)}>
                            <Button variant="outline" size="sm" className="flex items-center space-x-2 min-w-28 border-primary border-2">
                              <ExternalLink className="w-4 h-4" />
                              <span>{t('viewLarge')}</span>
                            </Button>
                          </Link>
                          <Button
                            variant="default"
                            size="sm"
                            className="flex items-center space-x-2"
                            onClick={() => copyToClipboard(`${window.location.origin}${getAlbumPath(`/galeria-en-vivo?code=${screenKey}`)}`, 'successMessageKey_copyLink')}
                          >
                            <LinkIcon className="w-4 h-4" />
                            {t('copyPublicLink')}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>

                  <Card style={cardSurfaceStyle}>
                    <CardHeader className="w-full">
                      <div className="flex w-full items-center justify-between">
                        <div>
                          <div className="flex items-center">
                            <Trophy className="mr-3 w-5 h-5 text-[hsl(var(--surface-fg))]" />
                            <div style={headingTopOffsetStyle}>
                              <CardTitle className='text-md md:text-lg'>{t('rankingTitle')}</CardTitle>
                            </div>
                          </div>
                          <p className="mr-4 mt-2 text-sm text-[hsl(var(--surface-muted-fg))]">
                            {t('screenContentDescriptionRanking')}
                          </p>
                        </div>
                        <div className="flex flex-col items-end space-y-2">
                          <Link to={getAlbumPath(`/los-mas-votados?code=${screenKey}`)} target='_new'>
                            <Button variant="outline" size="sm" className="flex items-center space-x-2 min-w-28 border-primary border-2">
                              <ExternalLink className="w-4 h-4" />
                              <span>{t('viewLarge')}</span>
                            </Button>
                          </Link>
                          <Button
                            variant="default"
                            size="sm"
                            className="flex items-center space-x-2"
                            onClick={() => copyToClipboard(`${window.location.origin}${getAlbumPath(`/los-mas-votados?code=${screenKey}`)}`, 'successMessageKey_copyLink')}
                          >
                            <LinkIcon className="w-4 h-4" />

                            {t('copyPublicLink')}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                  <div>
                    <img
                      src="/img/slideshow.png"
                      alt="Slideshow preview"
                      className="w-full rounded-lg shadow-md object-cover md:hidden pointer-events-none"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>


          {/* Quick Actions */}
          <Card style={cardSurfaceStyle}>
            <CardHeader>
              <div style={headingTopOffsetStyle}>
                <CardTitle className='text-md md:text-lg'>{t('quickActions')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Link to={getAlbumPath("/admin/usuarios")}>
                  <Button variant="outline" className="flex items-center space-x-2">
                    <Users className="w-4 h-4" />
                    <span>{t('manageUsers')}</span>
                  </Button>
                </Link>
                <Link to={getAlbumPath("/admin/fotos")}>
                  <Button variant="outline" className="flex items-center space-x-2">
                    <Images className="w-4 h-4" />
                    <span>{t('managePhotos')}</span>
                  </Button>
                </Link>
                <Link to={getAlbumPath("/album")}>
                  <Button variant="outline" className="flex items-center space-x-2">
                    <FolderHeart className="w-4 h-4" />
                    <span>{t('viewGallery')}</span>
                  </Button>
                </Link>
                {getBooleanSetting('enable_guestbook', false) &&
                  <Link to={getAlbumPath("/mensajes")}>
                    <Button variant="outline" className="flex items-center space-x-2">
                      <NotebookPen className="w-4 h-4" />
                      <span>{t('viewGuestbook')}</span>
                    </Button>
                  </Link>

                }


              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
